import {
  IInputEventHandler,
  InputEventType,
  InputMultiPointerEvent,
  InputPointerType,
  IPinchArgs,
  ISwipeArgs,
} from "../utils/input-handler/interface";
import { Viewer } from "../viewer";
import {
  IEditMeasurementParams,
  TMeasurementEditObserver,
  IMeasurementHandler,
  IMeasurementEditOnPositionUpdateObserver,
  IMeasurementEditOnUpdateCompleteObserver,
} from "./types";
import * as Config from "../config/ViewerConfig";
import { Object3D, Plane, Raycaster, Vector3 } from "three";
import * as Utils from "../utils/utils";
import { AutoAttenuablePin } from "../object/AutoAttenuablePin";

export class MeasurementEditObservable implements IInputEventHandler {
  private _viewer: Viewer;

  private _pinToMove: AutoAttenuablePin;
  private _planeToMove: Plane;

  private _editParams: IEditMeasurementParams;
  private _pickableObjects: Object3D[];
  private _observers: TMeasurementEditObserver[];
  private _measurementHandler: IMeasurementHandler;

  private _updatedPosition: Vector3;

  constructor(viewer: Viewer) {
    this._viewer = viewer;
    this._pickableObjects = [];
    this._observers = [];
    this._editParams = { dataflow: "stream", direction: "default" };
  }

  public startEditing(
    measurementHandler: IMeasurementHandler,
    params: IEditMeasurementParams,
    observers: TMeasurementEditObserver[]
  ) {
    this._measurementHandler = measurementHandler;
    this._pickableObjects = this._measurementHandler.getPickableObjects();
    this._editParams.dataflow = params.dataflow ? params.dataflow : "stream";
    this._editParams.direction = params.direction
      ? params.direction
      : "default";
    this._editParams = params;
    this._observers = observers;
    console.log("direction requested", params.direction);
    console.log("direction used", this._editParams.direction);
  }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ): void {
    let { clientX, clientY } = event;
    // if (event.pointerType === "touch") {
    //   //shift the point downward
    //   clientY += Config.TOUCH_OFFSET;
    // }
    switch (pointer.eventType) {
      case InputEventType.START_DRAG: {
        this._planeToMove = undefined;
        if (pointer.pointerType === InputPointerType.PRIMARY) {
          //intersection with pins
          const raycaster = new Raycaster();
          const coords = Utils.CalculateMousePosition(
            { clientX, clientY },
            this._viewer.getRenderer().domElement
          );
          raycaster.setFromCamera(coords, this._viewer.getCamera());
          const hits = raycaster.intersectObjects(this._pickableObjects);
          if (hits && hits.length > 0) {
            this._viewer.enableControl({ pan: false, rotate: false });
            this._pinToMove = hits[0].object as AutoAttenuablePin;
            if (this._editParams && this._editParams.direction === "xy") {
              this._planeToMove = new Plane(
                new Vector3(0, 0, 1),
                -1 * this._pinToMove.getPosition().z
              );
            } else if (this._editParams && this._editParams.direction === "z") {
              this._planeToMove = new Plane().setFromNormalAndCoplanarPoint(
                this._viewer.getCamera().getWorldDirection(new Vector3()),
                this._pinToMove.getPosition()
              );
            } else {
              console.log("Default Edit direction");
            }
          }
        }
        break;
      }
      case InputEventType.DRAGGED:
        if (this._pinToMove && this._editParams) {
          const raycaster = new Raycaster();
          raycaster.params.Points.threshold = 0.01;
          if (event.pointerType === "touch") {
            //shift the point downward
            clientY += Config.TOUCH_OFFSET;
          }
          const coords = Utils.CalculateMousePosition(
            { clientX, clientY },
            this._viewer.getRenderer().domElement
          );
          raycaster.setFromCamera(coords, this._viewer.getCamera());
          this._updatedPosition = undefined;
          if (this._planeToMove) {
            this._updatedPosition = raycaster.ray.intersectPlane(
              this._planeToMove,
              new Vector3()
            );
          } else {
            let { clientX, clientY } = event;
            this._updatedPosition = this._viewer.intersectPointCloud({
              clientX,
              clientY,
            });
          }

          if (this._updatedPosition) {
            if (this._editParams && this._editParams.direction === "z") {
              this._updatedPosition.set(
                this._pinToMove.getPosition().x,
                this._pinToMove.getPosition().y,
                this._updatedPosition.z
              );
            }
            const index = parseInt(this._pinToMove.getId(), 10);
            this._measurementHandler.onPositionUpdate(
              index,
              this._updatedPosition
            );
            if (this._editParams.dataflow === "stream") {
              this._observers.forEach((o) => {
                const observer = o as IMeasurementEditOnPositionUpdateObserver;
                observer.onPositionUpdate &&
                  observer.onPositionUpdate(
                    index,
                    this._updatedPosition,
                    this._measurementHandler.value
                  );
              });
            }
          }
        }
        break;
      case InputEventType.END_DRAG:
        this._viewer.enableControl({ pan: true, rotate: true });
        if (this._pinToMove) {
          const index = parseInt(this._pinToMove.getId(), 10);
          this._measurementHandler.onUpdateComplete(
            index,
            this._updatedPosition
          );
          if (this._editParams.dataflow === "latest_val_on_release") {
            this._observers.forEach((o) => {
              const observer = o as IMeasurementEditOnUpdateCompleteObserver;
              observer.onUpdateComplete &&
                observer.onUpdateComplete(
                  index,
                  this._updatedPosition,
                  this._measurementHandler.value
                );
            });
          }
        }
        this._pinToMove = undefined;
        break;
    }
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {
    console.warn("Multiple Pointer not implemented");
  }
}
